import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import {
  BookOpen,
  CheckCircle2,
  Layers,
  Lightbulb,
  Link2,
  Loader2,
  Save,
  ShieldCheck,
  Sparkles,
  Tag,
  User,
  X,
} from "lucide-react";
import {
  hyperlinkTooLong,
  INITIATIVE_LIST_ID,
  LIBRARY_LIST_ID,
  MAX_HYPERLINK_LENGTH,
  PLATFORM_LIST_ID,
  listStatusConfig,
  updateInitiativeItem,
  updateLibraryItem,
  updatePlatformItem,
} from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import { useListColumns, choicesFor, hintFor } from "../hooks/useListColumns";
import type {
  CatalogItem,
  InitiativeItem,
  LibraryItem,
  ListKind,
  PlatformItem,
} from "../types/navigator";
import { ConfirmDialog } from "./ConfirmDialog";

// Shared field/datalist primitives so we don't duplicate them across kinds.

function Field({
  label,
  required,
  hint,
  labelRight,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  labelRight?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="add-item-field">
      <span className="add-item-label-row">
        <span className="add-item-label">
          {label}
          {required ? (
            <span aria-hidden className="add-item-required">
              *
            </span>
          ) : null}
        </span>
        {labelRight}
      </span>
      {children}
      {hint ? <span className="add-item-hint">{hint}</span> : null}
    </label>
  );
}

function DataListInput({
  value,
  onChange,
  options,
  listId,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  listId: string;
  placeholder?: string;
}) {
  return (
    <>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        list={options.length ? listId : undefined}
        placeholder={placeholder}
      />
      {options.length ? (
        <datalist id={listId}>
          {options.map((opt) => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
      ) : null}
    </>
  );
}

function uniqueValues<T>(items: T[], pick: (i: T) => string): string[] {
  const set = new Set<string>();
  for (const item of items) {
    const v = pick(item).trim();
    if (v) set.add(v);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// Native <select> over a fixed choice set — matches the styled dropdowns used
// for closed (allowTextEntry:false) SharePoint choice columns.
function ChoiceSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled>
        {placeholder ?? "Select…"}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

// Per-line checkbox group for multi-choice ("checkBoxes") columns.
function CheckGroup({
  values,
  options,
  onToggle,
  ariaLabel,
}: {
  values: string[];
  options: string[];
  onToggle: (v: string) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="add-item-checkgroup"
      role="group"
      aria-label={ariaLabel}
      style={{ display: "flex", flexDirection: "column" }}
    >
      {options.map((opt) => {
        const checked = values.includes(opt);
        return (
          <label
            key={opt}
            className={`add-item-check${checked ? " is-checked" : ""}`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(opt)}
            />
            <span>{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

// Split a mapper's comma-joined multi-choice value back into an array of labels.
function splitMulti(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// Fallback choice sets, used only until the live column definitions load from
// SharePoint (or if that fetch fails). Kept in sync manually otherwise, so
// treat SharePoint's List settings as the source of truth.
const LIBRARY_ASSET_TYPE_CHOICES_FALLBACK = ["Prompt", "Agent", "App"];
const LIBRARY_AI_PLATFORM_CHOICES_FALLBACK = [
  "Copilot",
  "Custom App",
  "Egnyte",
  "Microsoft Excel",
  "Microsoft Outlook",
  "Microsoft PowerPoint",
  "Microsoft Teams",
  "Microsoft Word",
  "PowerBI",
  "SharePoint",
];
const LIBRARY_COMPLEXITY_CHOICES_FALLBACK = ["Beginner", "Intermediate", "Advanced"];
const LIBRARY_BENEFIT_CHOICES_FALLBACK = ["Efficiency", "Insights"];
const LIBRARY_DEPARTMENT_CHOICES_FALLBACK = [
  "Everyone",
  "Asset Management",
  "Audit & Compliance",
  "Communications",
  "Construction",
  "Customer Care / Warranty",
  "Customer Experience",
  "Finance & Accounting",
  "Human Resources",
  "Information Technology",
  "Investments",
  "Land Development",
  "Legal & Risk",
  "Marketing",
  "Project Management",
  "Purchasing",
  "Safety",
  "Sales",
];
const LIBRARY_LICENSE_CHOICES_FALLBACK = ["Free to Use", "License Required"];

// Fallback choice sets from the AI Platform list's column settings.
const PLATFORM_TYPE_CHOICES_FALLBACK = [
  "Website/Agent",
  "Desktop (requires install)",
  "Mobile",
  "API/MCP",
  "CLI/Terminal",
  "IDE/Plugin/Extension",
];
const PLATFORM_LICENSE_TYPE_CHOICES_FALLBACK = [
  "Enterprise",
  "Team",
  "Individual / Pro",
  "Free",
];
const PLATFORM_COMPLEXITY_CHOICES_FALLBACK = ["Beginner", "Intermediate", "Advanced"];
const PLATFORM_BROADER_CHOICES_FALLBACK = ["Yes", "No"];
const PLATFORM_APPROVED_USE_CHOICES_FALLBACK = ["Everyone", "IT", "Citizen Developers"];
const PLATFORM_COST_MODEL_CHOICES_FALLBACK = [
  "Per Named User",
  "Per Concurrent User",
  "Per Device",
  "Per Token",
  "Per Agent/Bot",
  "Per Tenant",
];

// Reports unsaved-edit state up to the parent modal. Each form snapshots its
// initial values on mount and compares the live form against that snapshot, so
// the parent can intercept backdrop / X dismissals before work is lost. A
// successful save clears the flag (saved === true) so the post-save auto-close
// doesn't trip the discard prompt.
function useDirtyTracker<T>(
  form: T,
  saved: boolean,
  onDirtyChange: (dirty: boolean) => void
) {
  const initialRef = useRef<string | null>(null);
  const snapshot = JSON.stringify(form);
  if (initialRef.current === null) initialRef.current = snapshot;

  const dirty = !saved && snapshot !== initialRef.current;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
}

// Status choices vary by kind — Library uses "Published", Initiative uses
// "Live", Platform uses "Approved". We pull the per-kind list from the API
// layer's listStatusConfig() rather than hardcoding one shared set.
// Admin can still type a custom value — the field is a datalist, not <select>.

const KIND_META: Record<
  ListKind,
  { label: string; icon: typeof BookOpen; modalClass: string; iconClass: string }
> = {
  library: {
    label: "AI Library",
    icon: BookOpen,
    modalClass: "modal-library",
    iconClass: "icon-library",
  },
  initiative: {
    label: "AI Initiative",
    icon: Lightbulb,
    modalClass: "modal-initiative",
    iconClass: "icon-initiative",
  },
  platform: {
    label: "AI Platform",
    icon: Layers,
    modalClass: "modal-platform",
    iconClass: "icon-platform",
  },
};

type Props = {
  item: CatalogItem;
  /** All loaded items of this kind — used to populate datalist suggestions. */
  siblings: CatalogItem[];
  onClose: () => void;
  onSaved: () => void;
};

type SubFormProps<T> = {
  item: T;
  siblings: T[];
  onClose: () => void;
  onSaved: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onSavingChange: (saving: boolean) => void;
};

/**
 * Admin edit modal. Routes to a per-kind form internally so we can keep the
 * field labels and helpers tuned to each list, while sharing the modal
 * chrome and submit flow. Status is editable on every kind — that's the main
 * extension over the Add modals (which lock new items to their list's
 * "awaiting review" status — "Under Review" for Library, "New" for
 * Initiative/Platform).
 *
 * Dismissing via the backdrop or the X button is guarded: if the active form
 * has unsaved edits, we surface a discard confirmation instead of closing
 * outright. The explicit Cancel button still closes immediately.
 */
export function EditItemModal({ item, siblings, onClose, onSaved }: Props) {
  const meta = KIND_META[item.kind];
  const Icon = meta.icon;

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Guarded dismiss: don't interrupt an in-progress save; prompt only when
  // the user has unsaved edits that would be lost.
  const requestClose = useCallback(() => {
    if (saving) return;
    if (dirty) setConfirmOpen(true);
    else onClose();
  }, [saving, dirty, onClose]);

  return (
    <>
      <div
        className="modal-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget) requestClose();
        }}
      >
        <div
          className={`modal ${meta.modalClass} add-item-modal edit-item-modal`}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-accent" aria-hidden />
          <div className="modal-head">
            <div className={`modal-head-icon ${meta.iconClass}`} aria-hidden>
              <Icon size={22} />
            </div>
            <div className="modal-head-text">
              <span className="modal-kind">{meta.label}</span>
              <h2 className="modal-title">Edit “{item.title || "Untitled"}”</h2>
              <p className="add-item-subtitle">
                Update any field — changes save immediately to SharePoint.
              </p>
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={requestClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {item.kind === "library" ? (
            <LibraryEditForm
              item={item}
              siblings={siblings.filter(
                (i): i is LibraryItem => i.kind === "library"
              )}
              onClose={onClose}
              onSaved={onSaved}
              onDirtyChange={setDirty}
              onSavingChange={setSaving}
            />
          ) : null}
          {item.kind === "initiative" ? (
            <InitiativeEditForm
              item={item}
              siblings={siblings.filter(
                (i): i is InitiativeItem => i.kind === "initiative"
              )}
              onClose={onClose}
              onSaved={onSaved}
              onDirtyChange={setDirty}
              onSavingChange={setSaving}
            />
          ) : null}
          {item.kind === "platform" ? (
            <PlatformEditForm
              item={item}
              siblings={siblings.filter(
                (i): i is PlatformItem => i.kind === "platform"
              )}
              onClose={onClose}
              onSaved={onSaved}
              onDirtyChange={setDirty}
              onSavingChange={setSaving}
            />
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Discard your changes?"
        message="You've made edits that haven't been saved yet. If you leave now, they'll be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="danger"
        autoFocusConfirm={false}
        onConfirm={() => {
          setConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

// ---------------- Library edit form ----------------

type LibraryFormState = {
  Title: string;
  Description: string;
  AssetType: string;
  AIPlatform: string;
  Complexity: string;
  BenefitType: string;
  Instructions: string;
  Department: string;
  LicenseRequired: string;
  AssetStatus: string;
  UrlHref: string;
  UrlLabel: string;
};

function LibraryEditForm({
  item,
  onClose,
  onSaved,
  onDirtyChange,
  onSavingChange,
}: SubFormProps<LibraryItem>) {
  const getToken = useGraphToken();
  const { columns } = useListColumns(LIBRARY_LIST_ID);
  const assetTypeChoices = useMemo(
    () => choicesFor(columns, "AssetType", LIBRARY_ASSET_TYPE_CHOICES_FALLBACK),
    [columns]
  );
  const aiPlatformChoices = useMemo(
    () => choicesFor(columns, "AIPlatform", LIBRARY_AI_PLATFORM_CHOICES_FALLBACK),
    [columns]
  );
  const complexityChoices = useMemo(
    () => choicesFor(columns, "Complexity", LIBRARY_COMPLEXITY_CHOICES_FALLBACK),
    [columns]
  );
  const benefitChoices = useMemo(
    () => choicesFor(columns, "BenefitType", LIBRARY_BENEFIT_CHOICES_FALLBACK),
    [columns]
  );
  const departmentChoices = useMemo(
    () => choicesFor(columns, "Department", LIBRARY_DEPARTMENT_CHOICES_FALLBACK),
    [columns]
  );
  const licenseChoices = useMemo(
    () => choicesFor(columns, "LicenseRequired", LIBRARY_LICENSE_CHOICES_FALLBACK),
    [columns]
  );
  const statusChoices = useMemo(() => {
    const cfg = listStatusConfig("library");
    return choicesFor(columns, cfg.field, cfg.choices);
  }, [columns]);
  const [form, setForm] = useState<LibraryFormState>(() => ({
    Title: item.title,
    Description: item.description,
    AssetType: item.assetType,
    AIPlatform: item.aiPlatform,
    Complexity: item.complexity,
    BenefitType: item.benefitType,
    Instructions: item.instructions,
    Department: item.department,
    LicenseRequired: item.license,
    AssetStatus: item.status || "Under Review",
    UrlHref: item.url ?? "",
    UrlLabel: item.urlLabel ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDirtyTracker(form, saved, onDirtyChange);

  const update = (key: keyof LibraryFormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Budget shrinks if a custom label is typed — Description defaults to
  // "Open" when blank, matching hyperlinkTooLong()'s own fallback.
  const urlMaxChars = MAX_HYPERLINK_LENGTH - (form.UrlLabel.trim() || "Open").length;

  const canSubmit = form.Title.trim().length > 0 && !saving;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    if (
      form.UrlHref.trim() &&
      hyperlinkTooLong(form.UrlHref.trim(), form.UrlLabel.trim())
    ) {
      setError(
        `Link URL is too long — SharePoint's Hyperlink field caps the URL and label combined at about ${MAX_HYPERLINK_LENGTH} characters. Try a shorter URL or a link-shortener.`
      );
      return;
    }
    setSaving(true);
    onSavingChange(true);
    try {
      const token = await getToken();
      await updateLibraryItem(token, item.id, {
        Title: form.Title.trim(),
        Description: form.Description.trim(),
        AssetType: form.AssetType.trim(),
        AIPlatform: form.AIPlatform.trim(),
        Complexity: form.Complexity.trim(),
        BenefitType: form.BenefitType.trim(),
        Instructions: form.Instructions.trim(),
        Department: form.Department.trim(),
        LicenseRequired: form.LicenseRequired.trim(),
        AssetStatus: form.AssetStatus.trim() || "Under Review",
        URL: form.UrlHref.trim()
          ? {
              url: form.UrlHref.trim(),
              label: form.UrlLabel.trim() || undefined,
            }
          : undefined,
      });
      setSaved(true);
      setTimeout(onSaved, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      onSavingChange(false);
    }
  };

  return (
    <>
      <form className="modal-body add-item-form" onSubmit={onSubmit}>
        <section className="add-item-section">
          <header className="add-item-section-head">
            <Sparkles size={14} aria-hidden />
            <h3>The basics</h3>
          </header>
          <Field label="Title" required>
            <input
              type="text"
              value={form.Title}
              onChange={(e) => update("Title", e.target.value)}
              required
              maxLength={255}
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={3}
              value={form.Description}
              onChange={(e) => update("Description", e.target.value)}
            />
          </Field>
          <Field
            label="Status"
            hint="Published items appear in the public catalog. Under Review hides them from non-admins."
          >
            <ChoiceSelect
              value={form.AssetStatus}
              onChange={(v) => update("AssetStatus", v)}
              options={statusChoices}
              placeholder="Select a status…"
            />
          </Field>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <Tag size={14} aria-hidden />
            <h3>Classify it</h3>
          </header>
          <div className="add-item-row">
            <Field label="Asset type">
              <ChoiceSelect
                value={form.AssetType}
                onChange={(v) => update("AssetType", v)}
                options={assetTypeChoices}
                placeholder="Select an asset type…"
              />
            </Field>
            <Field label="Platform">
              <ChoiceSelect
                value={form.AIPlatform}
                onChange={(v) => update("AIPlatform", v)}
                options={aiPlatformChoices}
                placeholder="Select a platform…"
              />
            </Field>
          </div>
          <div className="add-item-row">
            <Field label="Complexity">
              <ChoiceSelect
                value={form.Complexity}
                onChange={(v) => update("Complexity", v)}
                options={complexityChoices}
                placeholder="Select complexity…"
              />
            </Field>
            <Field label="Benefit type">
              <ChoiceSelect
                value={form.BenefitType}
                onChange={(v) => update("BenefitType", v)}
                options={benefitChoices}
                placeholder="Select a benefit type…"
              />
            </Field>
          </div>
          <div className="add-item-row">
            <Field label="Department">
              <ChoiceSelect
                value={form.Department}
                onChange={(v) => update("Department", v)}
                options={departmentChoices}
                placeholder="Select a department…"
              />
            </Field>
            <Field label="License">
              <ChoiceSelect
                value={form.LicenseRequired}
                onChange={(v) => update("LicenseRequired", v)}
                options={licenseChoices}
                placeholder="Select a license…"
              />
            </Field>
          </div>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <BookOpen size={14} aria-hidden />
            <h3>How to use it</h3>
          </header>
          <Field label="Instructions">
            <textarea
              rows={4}
              value={form.Instructions}
              onChange={(e) => update("Instructions", e.target.value)}
            />
          </Field>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <Link2 size={14} aria-hidden />
            <h3>Link (optional)</h3>
          </header>
          <div className="add-item-row">
            <Field
              label="Link URL"
              labelRight={
                <span
                  className={`add-item-charcount${
                    form.UrlHref.trim().length > urlMaxChars ? " is-over" : ""
                  }`}
                >
                  {form.UrlHref.trim().length}/{urlMaxChars} — cannot exceed {urlMaxChars} characters
                </span>
              }
            >
              <input
                type="url"
                value={form.UrlHref}
                onChange={(e) => update("UrlHref", e.target.value)}
                placeholder="https://…"
              />
            </Field>
            <Field label="Link label">
              <input
                type="text"
                value={form.UrlLabel}
                onChange={(e) => update("UrlLabel", e.target.value)}
              />
            </Field>
          </div>
        </section>

        {error ? (
          <div className="add-item-error" role="alert">
            <strong>Couldn't save.</strong> {error}
          </div>
        ) : null}
      </form>
      <EditFootBar
        saving={saving}
        saved={saved}
        canSubmit={canSubmit}
        onCancel={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
}

// ---------------- Initiative edit form ----------------

type InitiativeFormState = {
  Title: string;
  Yourname: string;
  Team: string;
  Region: string;
  Problem: string;
  Frequency: string;
  ImpactedToday: string;
  IfSolved: string;
  AnticipatedImpact: string;
  // Multi-select choice column — tracked as an array of selected labels so it
  // round-trips to SharePoint as an array (a comma-joined string 400s).
  ImpactType: string[];
  Status: string;
  ProposedSolution: string;
  ExistingExamples: string;
  DataInputs: string;
  ConstraintsRisks: string;
  AnythingElse: string;
  Notes: string;
};

// Fallback choice sets from the Initiative list's column settings, used only
// until the live column definitions load. Region, How-often, and
// Type-of-impact are allowTextEntry:false, so an admin must pick a valid
// value or the PATCH is rejected. (Priority is a 1-5 number set by the
// review tooling, not editable here — same as the Add modal.)
const INITIATIVE_REGION_CHOICES_FALLBACK = [
  "Atlanta",
  "Austin",
  "Corporate",
  "Calgary",
  "Edmonton",
  "NorCal",
  "Washington",
  "Denver",
  "Toronto",
  "Phoenix",
  "SoCal",
  "Houston",
  "Raleigh / Durham",
  "Charleston",
  "Tampa",
  "Wilmington",
  "Dallas",
  "Portland",
  "Seattle",
  "Charlotte",
  "Spokane",
  "Sacramento",
  "Columbus",
  "New Jersey",
  "Delaware",
  "Virginia",
  "Puget Sound",
  "Weymouth",
];

const INITIATIVE_FREQUENCY_CHOICES_FALLBACK = [
  "Daily",
  "Weekly",
  "Monthly",
  "Occasionally",
  "One-time, but high impact",
];

const INITIATIVE_IMPACT_TYPE_CHOICES_FALLBACK = [
  "Time savings / efficiency",
  "Cost reduction (material, licensing, budget, etc.)",
  "Improved insights and decision quality",
  "Risk reduction",
  "Scalability",
  "Better customer or partner experience",
  "Other",
];

function InitiativeEditForm({
  item,
  siblings,
  onClose,
  onSaved,
  onDirtyChange,
  onSavingChange,
}: SubFormProps<InitiativeItem>) {
  const getToken = useGraphToken();
  const { columns } = useListColumns(INITIATIVE_LIST_ID);
  const regionChoices = useMemo(
    () => choicesFor(columns, "Region_x002f_Division", INITIATIVE_REGION_CHOICES_FALLBACK),
    [columns]
  );
  const frequencyChoices = useMemo(
    () => choicesFor(columns, "Howoftendoesthisproblemoccur_x00", INITIATIVE_FREQUENCY_CHOICES_FALLBACK),
    [columns]
  );
  const impactTypeChoices = useMemo(
    () => choicesFor(columns, "Whattypeofimpacttodoyouexpect_x0", INITIATIVE_IMPACT_TYPE_CHOICES_FALLBACK),
    [columns]
  );
  const statusChoices = useMemo(() => {
    const cfg = listStatusConfig("initiative");
    return choicesFor(columns, cfg.field, cfg.choices);
  }, [columns]);
  const [form, setForm] = useState<InitiativeFormState>(() => ({
    Title: item.title,
    Yourname: item.submittedBy,
    Team: item.team,
    Region: item.region,
    Problem: item.problem,
    Frequency: item.problemFrequency,
    ImpactedToday: item.impactedToday,
    IfSolved: item.ifSolved,
    AnticipatedImpact: item.anticipatedImpact,
    ImpactType: item.impactTypes,
    // "New" is the Initiative list's real default — "Pending" is not a valid
    // choice and writing it back would 400 the whole save.
    Status: item.status || "New",
    ProposedSolution: item.proposedSolution,
    ExistingExamples: item.existingExamples,
    DataInputs: item.dataInputs,
    ConstraintsRisks: item.constraintsRisks,
    AnythingElse: item.anythingElse,
    Notes: item.notes,
  }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDirtyTracker(form, saved, onDirtyChange);

  // Team / function is still free-text on the list, so keep suggesting
  // previously-used values. Region, How-often, and Type-of-impact are closed
  // choice sets and use their fixed vocabularies instead.
  const teams = useMemo(
    () => uniqueValues(siblings, (i) => i.team),
    [siblings]
  );

  const update = (key: keyof InitiativeFormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleImpact = (value: string) =>
    setForm((prev) => ({
      ...prev,
      ImpactType: prev.ImpactType.includes(value)
        ? prev.ImpactType.filter((v) => v !== value)
        : [...prev.ImpactType, value],
    }));

  const canSubmit = form.Title.trim().length > 0 && !saving;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSaving(true);
    onSavingChange(true);
    try {
      const token = await getToken();
      await updateInitiativeItem(token, item.id, {
        Title: form.Title.trim(),
        Yourname: form.Yourname.trim(),
        Yourteam_x002f_function: form.Team.trim(),
        Region_x002f_Division: form.Region.trim(),
        Whatproblemareyoutryingtosolve: form.Problem.trim(),
        Howoftendoesthisproblemoccur_x00: form.Frequency.trim(),
        Whoisimpactedbythisproblemtoday_: form.ImpactedToday.trim(),
        Ifthisproblemweresolved_x002c_wh: form.IfSolved.trim(),
        Pleaseexplaintheanticipatedimpac: form.AnticipatedImpact.trim(),
        // Multi-choice column expects an array, not a comma-joined string.
        Whattypeofimpacttodoyouexpect_x0: form.ImpactType,
        ProposedSolution: form.ProposedSolution.trim(),
        ExistingExamples: form.ExistingExamples.trim(),
        DataInputs: form.DataInputs.trim(),
        ConstraintsRisks: form.ConstraintsRisks.trim(),
        AnythingElse: form.AnythingElse.trim(),
        Notes: form.Notes.trim(),
        Status: form.Status.trim() || "New",
      });
      setSaved(true);
      setTimeout(onSaved, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      onSavingChange(false);
    }
  };

  return (
    <>
      <form className="modal-body add-item-form" onSubmit={onSubmit}>
        <section className="add-item-section">
          <header className="add-item-section-head">
            <User size={14} aria-hidden />
            <h3>Who & what</h3>
          </header>
          <Field label="Initiative title" required>
            <input
              type="text"
              value={form.Title}
              onChange={(e) => update("Title", e.target.value)}
              required
              maxLength={255}
            />
          </Field>
          <div className="add-item-row">
            <Field label="Submitter">
              <input
                type="text"
                value={form.Yourname}
                onChange={(e) => update("Yourname", e.target.value)}
              />
            </Field>
            <Field label="Team / function">
              <DataListInput
                value={form.Team}
                onChange={(v) => update("Team", v)}
                options={teams}
                listId="edit-in-team"
              />
            </Field>
          </div>
          <div className="add-item-row">
            <Field label="Region / division">
              <select
                value={form.Region}
                onChange={(e) => update("Region", e.target.value)}
              >
                <option value="" disabled>
                  Select a region…
                </option>
                {regionChoices.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Status"
              hint="Live initiatives are visible in the public list."
            >
              <select
                value={form.Status}
                onChange={(e) => update("Status", e.target.value)}
              >
                <option value="" disabled>
                  Select a status…
                </option>
                {statusChoices.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <Sparkles size={14} aria-hidden />
            <h3>The problem</h3>
          </header>
          <Field label="Problem">
            <textarea
              rows={3}
              value={form.Problem}
              onChange={(e) => update("Problem", e.target.value)}
            />
          </Field>
          <div className="add-item-row">
            <Field label="How often">
              <select
                value={form.Frequency}
                onChange={(e) => update("Frequency", e.target.value)}
              >
                <option value="" disabled>
                  Select frequency…
                </option>
                {frequencyChoices.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Who's impacted today">
              <input
                type="text"
                value={form.ImpactedToday}
                onChange={(e) => update("ImpactedToday", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <Sparkles size={14} aria-hidden />
            <h3>If it were solved</h3>
          </header>
          <Field label="What would change">
            <textarea
              rows={3}
              value={form.IfSolved}
              onChange={(e) => update("IfSolved", e.target.value)}
            />
          </Field>
          <Field label="Anticipated impact">
            <textarea
              rows={2}
              value={form.AnticipatedImpact}
              onChange={(e) => update("AnticipatedImpact", e.target.value)}
            />
          </Field>
          <Field label="Type of impact" hint="Select all that apply.">
            <div
              className="add-item-checkgroup"
              role="group"
              aria-label="Type of impact"
              style={{ display: "flex", flexDirection: "column" }}
            >
              {impactTypeChoices.map((opt) => {
                const checked = form.ImpactType.includes(opt);
                return (
                  <label
                    key={opt}
                    className={`add-item-check${checked ? " is-checked" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleImpact(opt)}
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          </Field>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <Tag size={14} aria-hidden />
            <h3>Solution & context</h3>
          </header>
          <Field label="Proposed solution">
            <textarea
              rows={3}
              value={form.ProposedSolution}
              onChange={(e) => update("ProposedSolution", e.target.value)}
            />
          </Field>
          <Field label="Existing examples">
            <textarea
              rows={2}
              value={form.ExistingExamples}
              onChange={(e) => update("ExistingExamples", e.target.value)}
            />
          </Field>
          <div className="add-item-row">
            <Field label="Data / inputs">
              <textarea
                rows={2}
                value={form.DataInputs}
                onChange={(e) => update("DataInputs", e.target.value)}
              />
            </Field>
            <Field label="Constraints / risks">
              <textarea
                rows={2}
                value={form.ConstraintsRisks}
                onChange={(e) => update("ConstraintsRisks", e.target.value)}
              />
            </Field>
            <Field label="Anything else we should know?">
              <textarea
                rows={2}
                value={form.AnythingElse}
                onChange={(e) => update("AnythingElse", e.target.value)}
                placeholder={hintFor(columns, "Anythingelseweshouldknow_x003f_", "Compliance, sensitive data, dependencies, or anything that didn't fit above.")}
              />
            </Field>
          </div>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <ShieldCheck size={14} aria-hidden />
            <h3>Admin notes</h3>
          </header>
          <Field label="Notes" hint="Internal only — never shown to the submitter or on the public item card.">
            <textarea
              rows={3}
              value={form.Notes}
              onChange={(e) => update("Notes", e.target.value)}
            />
          </Field>
        </section>

        {error ? (
          <div className="add-item-error" role="alert">
            <strong>Couldn't save.</strong> {error}
          </div>
        ) : null}
      </form>
      <EditFootBar
        saving={saving}
        saved={saved}
        canSubmit={canSubmit}
        onCancel={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
}

// ---------------- Platform edit form ----------------

type PlatformFormState = {
  Title: string;
  Purpose: string;
  Notes: string;
  PlatformType: string;
  LicenseType: string;
  Complexity: string;
  UsedbybroaderBrookfield: string;
  // Multi-choice columns — tracked as arrays so they round-trip to SharePoint
  // as arrays (a bare string 500s on these columns).
  ApprovedUse: string[];
  CostModel: string[];
  Status: string;
};

function PlatformEditForm({
  item,
  onClose,
  onSaved,
  onDirtyChange,
  onSavingChange,
}: SubFormProps<PlatformItem>) {
  const getToken = useGraphToken();
  const { columns } = useListColumns(PLATFORM_LIST_ID);
  const platformTypeChoices = useMemo(
    () => choicesFor(columns, "PlatformType", PLATFORM_TYPE_CHOICES_FALLBACK),
    [columns]
  );
  const licenseTypeChoices = useMemo(
    () => choicesFor(columns, "LicenseType", PLATFORM_LICENSE_TYPE_CHOICES_FALLBACK),
    [columns]
  );
  const complexityChoices = useMemo(
    () => choicesFor(columns, "Complexity", PLATFORM_COMPLEXITY_CHOICES_FALLBACK),
    [columns]
  );
  const broaderChoices = useMemo(
    () => choicesFor(columns, "UsedbybroaderBrookfield", PLATFORM_BROADER_CHOICES_FALLBACK),
    [columns]
  );
  const approvedUseChoices = useMemo(
    () => choicesFor(columns, "ApprovedUse", PLATFORM_APPROVED_USE_CHOICES_FALLBACK),
    [columns]
  );
  const costModelChoices = useMemo(
    () => choicesFor(columns, "CostModel", PLATFORM_COST_MODEL_CHOICES_FALLBACK),
    [columns]
  );
  const statusChoices = useMemo(() => {
    const cfg = listStatusConfig("platform");
    return choicesFor(columns, cfg.field, cfg.choices);
  }, [columns]);
  const [form, setForm] = useState<PlatformFormState>(() => ({
    Title: item.title,
    Purpose: item.purpose,
    Notes: item.keyCapabilities,
    PlatformType: item.platformType,
    LicenseType: item.licenseType,
    Complexity: item.complexity,
    UsedbybroaderBrookfield: item.usedByBroaderBrookfield,
    ApprovedUse: splitMulti(item.approvedUse),
    CostModel: splitMulti(item.costModel),
    // "New" is the Platform list's real default — "Pending" is not a valid
    // choice and writing it back would reject the save.
    Status: item.status || "New",
  }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDirtyTracker(form, saved, onDirtyChange);

  const update = (key: keyof PlatformFormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleMulti = (key: "ApprovedUse" | "CostModel", value: string) =>
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));

  const canSubmit = form.Title.trim().length > 0 && !saving;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSaving(true);
    onSavingChange(true);
    try {
      const token = await getToken();
      await updatePlatformItem(token, item.id, {
        Title: form.Title.trim(),
        Purpose: form.Purpose.trim(),
        Notes: form.Notes.trim(),
        PlatformType: form.PlatformType.trim(),
        LicenseType: form.LicenseType.trim(),
        Complexity: form.Complexity.trim(),
        UsedbybroaderBrookfield: form.UsedbybroaderBrookfield.trim(),
        ApprovedUse: form.ApprovedUse,
        CostModel: form.CostModel,
        Status: form.Status.trim() || "New",
      });
      setSaved(true);
      setTimeout(onSaved, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      onSavingChange(false);
    }
  };

  return (
    <>
      <form className="modal-body add-item-form" onSubmit={onSubmit}>
        <section className="add-item-section">
          <header className="add-item-section-head">
            <Sparkles size={14} aria-hidden />
            <h3>The basics</h3>
          </header>
          <Field label="Title" required>
            <input
              type="text"
              value={form.Title}
              onChange={(e) => update("Title", e.target.value)}
              required
              maxLength={255}
            />
          </Field>
          <Field label="Purpose">
            <textarea
              rows={3}
              value={form.Purpose}
              onChange={(e) => update("Purpose", e.target.value)}
            />
          </Field>
          <Field label="Key capabilities">
            <textarea
              rows={3}
              value={form.Notes}
              onChange={(e) => update("Notes", e.target.value)}
            />
          </Field>
          <Field
            label="Status"
            hint="Approved platforms appear in the public catalog."
          >
            <ChoiceSelect
              value={form.Status}
              onChange={(v) => update("Status", v)}
              options={statusChoices}
              placeholder="Select a status…"
            />
          </Field>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <Tag size={14} aria-hidden />
            <h3>Classify it</h3>
          </header>
          <div className="add-item-row">
            <Field label="Platform type">
              <ChoiceSelect
                value={form.PlatformType}
                onChange={(v) => update("PlatformType", v)}
                options={platformTypeChoices}
                placeholder="Select a platform type…"
              />
            </Field>
            <Field label="Plan type">
              <ChoiceSelect
                value={form.LicenseType}
                onChange={(v) => update("LicenseType", v)}
                options={licenseTypeChoices}
                placeholder="Select a plan type…"
              />
            </Field>
          </div>
          <div className="add-item-row">
            <Field label="Skill level">
              <ChoiceSelect
                value={form.Complexity}
                onChange={(v) => update("Complexity", v)}
                options={complexityChoices}
                placeholder="Select a skill level…"
              />
            </Field>
            <Field label="Used by BAM">
              <ChoiceSelect
                value={form.UsedbybroaderBrookfield}
                onChange={(v) => update("UsedbybroaderBrookfield", v)}
                options={broaderChoices}
                placeholder="Select one…"
              />
            </Field>
          </div>
          <Field label="Billing / cost model" hint="Select all that apply.">
            <CheckGroup
              values={form.CostModel}
              options={costModelChoices}
              onToggle={(v) => toggleMulti("CostModel", v)}
              ariaLabel="Billing / cost model"
            />
          </Field>
        </section>

        <section className="add-item-section">
          <header className="add-item-section-head">
            <ShieldCheck size={14} aria-hidden />
            <h3>Approval & governance</h3>
          </header>
          <Field label="Approved users" hint="Select all that apply.">
            <CheckGroup
              values={form.ApprovedUse}
              options={approvedUseChoices}
              onToggle={(v) => toggleMulti("ApprovedUse", v)}
              ariaLabel="Approved users"
            />
          </Field>
        </section>

        {error ? (
          <div className="add-item-error" role="alert">
            <strong>Couldn't save.</strong> {error}
          </div>
        ) : null}
      </form>
      <EditFootBar
        saving={saving}
        saved={saved}
        canSubmit={canSubmit}
        onCancel={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
}

function EditFootBar({
  saving,
  saved,
  canSubmit,
  onCancel,
  onSubmit,
}: {
  saving: boolean;
  saved: boolean;
  canSubmit: boolean;
  onCancel: () => void;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <div className="modal-foot">
      <button
        type="button"
        className="btn btn-ghost"
        onClick={onCancel}
        disabled={saving}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={!canSubmit}
        onClick={onSubmit as unknown as () => void}
      >
        {saving ? (
          <>
            <Loader2 size={14} className="add-item-spin" /> Saving…
          </>
        ) : saved ? (
          <>
            <CheckCircle2 size={14} /> Saved
          </>
        ) : (
          <>
            <Save size={14} /> Save changes
          </>
        )}
      </button>
    </div>
  );
}
